import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { Body1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { FormSubmitValues } from '@utils/formValidation'

import { createFormField } from '@global/formField/FormField'
import { createFormPasswordField } from '@global/formField/FormPasswordField'

import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'

import styles from './LoginPage.module.scss'

export interface LoginFormValues {
  email: string
  password: string
}

const PasswordField = createFormPasswordField<LoginFormValues>()
const EmailField = createFormField<LoginFormValues>()

const LoginFormUI: React.FunctionComponent<FormikProps<LoginFormValues> & OuterProps> = (props) => {
  const { handleSubmit, isSubmitting, setSubmitting, isValid, dirty, values } = props
  useOnLocaleSwitch(() => props.validateForm())
  const { t } = useTranslation()

  const handleSubmitForm = (event: SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Form className={styles.form} onSubmit={handleSubmitForm}>
      <FieldWrapper>
        <EmailField
          name={'email'}
          label={t('Email Address')}
          type='email'
          required
          hidePlaceholderAsterisk
        />
        <PasswordField
          name='password'
          type={'password'}
          label={t('Password')}
          required
          hidePlaceholderAsterisk
        />
        <div className={styles.action}>
          <Link to='/forgot-password' state={{ email: values.email }} size='lg'>
            {t('Forgot Password.Forgot Password')}
          </Link>
        </div>
      </FieldWrapper>
      <Box mt='x40' mb='x40' fullWidth>
        <Button.Primary
          type='submit'
          size='lg'
          fullWidth
          loading={isSubmitting}
          disabled={!(isValid && dirty)}
        >
          {t('Log in')}
        </Button.Primary>
      </Box>
      {props.entity !== TickmillCompaniesEnum.TICKMILL_PA && (
        <div className={styles.signup}>
          <Body1>{t('Not a member')}</Body1>
          <Link to='/sign-up/account-type' size='lg'>
            {t('Sign up.Sign up here')}
          </Link>
        </div>
      )}
    </Form>
  )
}

interface OuterProps {
  entity: TickmillCompaniesEnum
  onSubmit(values: FormSubmitValues<LoginFormValues>): Promise<void>
}

export const LoginForm = withFormik<OuterProps, LoginFormValues>({
  mapPropsToValues: () => {
    return {
      email: '',
      password: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<LoginFormValues> = {}
    if (!values.email) {
      errors.email = t('Validation.Required')
    }
    if (!values.password) {
      errors.password = t('Validation.Required')
    }
    return errors
  },
  enableReinitialize: true,
})(LoginFormUI)
